import {
  ListenableSubscribable,
  SOCKET_NO_ACTIVITY_MS,
  SubscribableReconnectingSocket,
  SubscribableReconnectingSocketEvent,
  Subscription,
} from '@benzinga/subscribable';
import { ScannerProtos } from './scanner_protos';
import { Session } from '@benzinga/session';
import { SafeError } from '@benzinga/safe-await';
import { ScannerEnvironment } from './environment';

interface ScannerSocketErrorEvent {
  error: SafeError;
  errorType:
    | 'scanner:error_opening_connection'
    | 'scanner:out_of_sequence'
    | 'scanner:rowUpdates_is_null'
    | 'scanner:error_connecting';
  type: 'error';
}

interface ScannerSocketStatusEvent {
  status: 'connected' | 'disconnected' | 'reconnecting' | 'reconnected' | 'close';
  type: 'on-status-change';
}

interface ScannerRowUpdateEvent {
  type: 'row_update';
  rows: ScannerProtos.IRowUpdatesV2;
}

type ScannerSocketEvent = ScannerRowUpdateEvent | ScannerSocketErrorEvent | ScannerSocketStatusEvent;

export class ScannerSocket extends ListenableSubscribable<ScannerSocketEvent> {
  private socket: SubscribableReconnectingSocket<ArrayBuffer>;
  private socketSubscription?: Subscription<SubscribableReconnectingSocket<ArrayBuffer>>;
  private pendingResponseSubscription?: Subscription<SubscribableReconnectingSocket<ArrayBuffer>>;

  private requestId = 1;

  private config: ScannerProtos.IScannerConfig;
  private lastRowUpdatesSequence = 0;

  constructor(session: Session, config: ScannerProtos.IScannerConfig) {
    super();
    this.config = config;
    // Clone so each socket owns its URL; mutating the shared env URL races other scanners.
    const url = new URL(session.getEnvironment(ScannerEnvironment).socketUrl.toString());
    url.searchParams.set('apikey', session.getEnvironment(ScannerEnvironment).key);
    this.socket = new SubscribableReconnectingSocket(
      url,
      { binaryType: 'arraybuffer' },
      undefined,
      SOCKET_NO_ACTIVITY_MS,
    );
  }

  protected onFirstSubscription(): void {
    if (this.socketSubscription === undefined) {
      this.socketSubscription = this.socket.subscribe(this.onMessage);
    }
    this.socket.open();
  }

  protected onZeroSubscriptions(): void {
    this.clearPendingResponseSubscription();
    this.socket.close();
    if (this.socketSubscription !== undefined) {
      this.socketSubscription.unsubscribe();
      this.socketSubscription = undefined;
    }
  }

  private onMessage = (event: SubscribableReconnectingSocketEvent<ArrayBuffer>): void => {
    switch (event.type) {
      case 'disconnected':
      case 'reconnecting':
      case 'close':
        this.dispatch({
          status: event.type,
          type: 'on-status-change',
        });
        break;
      case 'error':
        this.dispatch({
          error: event.errorEvent,
          errorType: 'scanner:error_connecting',
          type: 'error',
        });
        break;
      case 'reconnected':
        this.startScannerSession('reconnected');
        break;
      case 'open':
        this.startScannerSession('connected');
        break;
      case 'response': {
        const msg = ScannerProtos.Response.decode(new Uint8Array(event.msg));
        switch (msg.type) {
          case 'heartbeat':
            return;
          case 'rowUpdatesV2': {
            const rowUpdates = msg.rowUpdatesV2;
            if (rowUpdates) {
              const expectedSequence = ++this.lastRowUpdatesSequence;
              // Normalize protobuf int64 (number | Long) before comparing.
              if (Number(rowUpdates.sequence) !== expectedSequence) {
                this.dispatch({
                  error: new SafeError('rowUpdate Out of sequence', 'scanner:out_of_sequence'),
                  errorType: 'scanner:out_of_sequence',
                  type: 'error',
                });
                this.socket.reconnect();
              } else {
                this.dispatch({
                  rows: rowUpdates,
                  type: 'row_update',
                });
              }
            } else {
              this.dispatch({
                error: new SafeError('rowUpdates is null', 'scanner:rowUpdates_is_null'),
                errorType: 'scanner:rowUpdates_is_null',
                type: 'error',
              });
            }
            break;
          }
          default:
            console.warn(`Scanner socket: unknown message type: ${msg.type}`, msg);
        }
      }
    }
  };

  private startScannerSession = (status: 'connected' | 'reconnected'): void => {
    this.clearPendingResponseSubscription();
    this.lastRowUpdatesSequence = 0;
    const request = ScannerProtos.WebSocketRequest.create({
      scannerRequest: {
        scanner: this.config,
      },
    });
    this.dispatch({
      status,
      type: 'on-status-change',
    });
    this.send(request, response => {
      if (response.error) {
        this.dispatch({
          error: new SafeError(response.error.message ?? '', 'scanner:error_opening_connection', response.error),
          errorType: 'scanner:error_opening_connection',
          type: 'error',
        });
      }
    });
  };

  private clearPendingResponseSubscription = (): void => {
    this.pendingResponseSubscription?.unsubscribe();
    this.pendingResponseSubscription = undefined;
  };

  private send = (request: ScannerProtos.WebSocketRequest, onResponse: (r: ScannerProtos.Response) => void) => {
    request.id = this.requestId++;
    this.clearPendingResponseSubscription();
    this.pendingResponseSubscription = this.socket.subscribe(event => {
      switch (event.type) {
        case 'response': {
          const msg = ScannerProtos.Response.decode(new Uint8Array(event.msg));
          if (msg.id === request.id) {
            onResponse(msg);
            this.clearPendingResponseSubscription();
          }
          break;
        }
      }
    });
    this.socket.send(ScannerProtos.WebSocketRequest.encode(request).finish());
  };
}
